import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { getDemoUrl } from '../lib/urls';

export function MarketingLanding() {
  return (
    <div className="min-h-screen bg-sand text-oud-900" dir="rtl">
      {/* Hero — video covers header + hero content as one seamless block */}
      <section className="relative overflow-hidden">
        {/* Video background — scaled slightly to hide edge artifacts */}
        <div className="absolute -inset-1 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover scale-[1.02]"
            src="/showcase.mp4"
          />
        </div>
        {/* Dot pattern over video — larger/brighter than default najdi-pattern for dark bg */}
        <div
          className="absolute -inset-1 z-[1] pointer-events-none"
          aria-hidden
          style={{
            backgroundImage:
              'radial-gradient(circle, rgba(255,255,255,0.25) 1.2px, transparent 1.2px), radial-gradient(circle, rgba(255,255,255,0.25) 1.2px, transparent 1.2px)',
            backgroundSize: '20px 20px',
            backgroundPosition: '0 0, 10px 10px',
          }}
        />
        {/* Dark tint + seamless fade into page bg */}
        <div
          className="absolute -inset-1 z-[1]"
          style={{
            background: 'linear-gradient(to bottom, rgba(46,29,16,0.55) 0%, rgba(46,29,16,0.4) 35%, rgba(46,29,16,0.25) 60%, rgba(251,247,242,0.85) 82%, #fbf7f2 95%)',
          }}
        />

        {/* Header — floats on top of the video, no background */}
        <header className="relative z-[2]">
          <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <img src="/logo.png" alt="سنديان" className="h-6 w-auto brightness-0 invert drop-shadow" />
              <span className="font-black text-xl text-white drop-shadow">سنديان</span>
            </span>
            <div className="flex items-center gap-3">
              <a href="#pricing" className="text-sm font-bold text-white/70 hover:text-white hidden sm:block drop-shadow-sm transition-colors">الأسعار</a>
              <Link to="/admin" className="text-sm font-bold text-white/90 bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-xl hover:bg-white/20 transition-all shadow-sm">
                لوحة التحكم
              </Link>
            </div>
          </div>
        </header>

        {/* Hero content */}
        <div className="relative z-[2] max-w-5xl mx-auto px-5 pt-16 pb-40">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto text-center space-y-5"
          >
            <h1 className="text-4xl md:text-[3.25rem] font-black leading-[1.1] tracking-tight text-white drop-shadow-md">
              الأداة الأولى لتسعير الأثاث
              <br />
              وتوليد العملاء المحتملين
            </h1>

            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-gold-soft px-4 py-1.5 text-xs font-bold tracking-wider shadow-sm">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-gold opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
              </span>
              مدعوم بالذكاء الاصطناعي
            </span>

            <p className="text-lg text-white/85 max-w-lg mx-auto leading-relaxed drop-shadow-sm">
              من صورة إلى تسعيرة إلى طلب جاهز بالتفاصيل — تلقائيًا
            </p>

            <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl px-5 py-3 shadow-lg">
              <span className="text-3xl font-black text-gold-soft leading-none drop-shadow">٧٨٪</span>
              <span className="text-sm text-white/75 text-right leading-snug">
                من العملاء يشترون من أول<br />من يسعّرهم · سنديان يسعّر خلال ثوانٍ
              </span>
            </div>

            <div className="pt-2">
              <Link to="/signup"
                className="cta-primary backdrop-blur-lg text-white px-10 py-4 rounded-2xl text-lg font-bold active:scale-[0.98]">
                ابدأ تجربتك المجانية
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trusted by */}
      <TrustedByMarquee />

      {/* What is Sindian */}
      <section className="bg-cream/50 py-16">
        <div className="max-w-5xl mx-auto px-5">
          <h2 className="text-center text-2xl font-black mb-3">ماذا يقدّم سنديان؟</h2>
          <p className="text-center text-oud-600 mb-10 max-w-xl mx-auto">
            يحلّل أسعار السوق تلقائيًا ويقدّم لعملائك تسعيرة تنافسية تناسب هوامشك — ويحوّل كل استفسار إلى طلب
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: 'تسعيرة فورية بالذكاء الاصطناعي',
                desc: 'العميل يرفع صورة أي قطعة أثاث — والنظام يحلّلها تلقائيًا ويقدم تسعيرة تنافسية مبنية على أسعار السوق وإعدادات التسعير الخاصة بك',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                  </svg>
                ),
              },
              {
                title: 'توليد عملاء محتملين',
                desc: 'كل تسعيرة تتحول إلى فرصة بيع. العميل يضيف القطع لسلة الطلب ويرسل بياناته — وأنت تستلم طلبًا جاهزًا بالتفاصيل',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <line x1="19" y1="8" x2="19" y2="14" />
                    <line x1="22" y1="11" x2="16" y2="11" />
                  </svg>
                ),
              },
              {
                title: 'موقعك الخاص جاهز بضغطة',
                desc: 'صفحة احترافية بهوية نشاطك، رابط مخصص، شعارك، ألوانك — جاهزة لمشاركتها مع العملاء وتخصيص كامل من لوحة تحكم عربية',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                  </svg>
                ),
              },
            ].map((f) => (
              <div key={f.title} className="bg-sand rounded-3xl p-6 border border-oud-100/60 shadow-soft">
                <div className="w-11 h-11 rounded-xl bg-oud-800 text-gold-soft flex items-center justify-center mb-3">{f.icon}</div>
                <h3 className="text-lg font-black text-oud-900 mb-2">{f.title}</h3>
                <p className="text-sm text-oud-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-cream/50 py-16" id="pricing">
        <div className="max-w-5xl mx-auto px-5">
          <h2 className="text-center text-2xl font-black mb-3">الأسعار</h2>
          <p className="text-center text-oud-600 mb-10">ابدأ مجانًا — ادفع فقط إذا حققت نتائج</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <div className="bg-sand rounded-3xl p-8 border border-oud-100 shadow-soft">
              <h3 className="text-lg font-black">تجربة مجانية</h3>
              <p className="text-4xl font-black text-gold mt-4">مجانًا</p>
              <p className="text-sm text-oud-600 mt-1">بدون بطاقة ائتمانية</p>
              <ul className="mt-6 space-y-2 text-sm text-oud-700">
                <li>✓ جميع المميزات</li>
                <li>✓ تسعيرات غير محدودة</li>
                <li>✓ لوحة تحكم كاملة</li>
                <li>✓ دعم فني</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-oud-800 to-oud-900 rounded-3xl p-8 text-sand shadow-quote relative overflow-hidden">
              <div className="absolute inset-0 najdi-pattern opacity-10 pointer-events-none" />
              <div className="relative">
                <h3 className="text-lg font-black">الاشتراك السنوي</h3>
                <p className="text-4xl font-black text-gold-soft mt-2">٤٬٤٠٠ <span className="text-lg">ر.س / سنة</span></p>
                <div className="mt-3 bg-gold/15 border border-gold-soft/25 rounded-xl px-4 py-2.5 flex items-center gap-3">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-gold-soft flex-none" aria-hidden>
                    <path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3Z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                  <span className="text-sm text-gold-soft font-bold leading-snug">ضمان استرداد كامل إذا لم تحصل على عملاء محتملين بقيمة اشتراكك</span>
                </div>
                <ul className="mt-6 space-y-2 text-sm text-cream/80">
                  <li>✓ كل شيء في التجربة المجانية</li>
                  <li>✓ نطاق مخصص</li>
                  <li>✓ تقارير متقدمة</li>
                  <li>✓ أولوية في الدعم الفني</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-5 py-16 text-center">
        <h2 className="text-3xl font-black mb-4">جاهز تبدأ؟</h2>
        <p className="text-oud-600 mb-6">سجّل نشاطك وابدأ باستقبال الطلبات خلال دقائق</p>
        <div className="flex flex-col items-center gap-3">
          <Link to="/signup"
            className="inline-block bg-oud-800 text-sand px-8 py-4 rounded-2xl text-lg font-bold shadow-soft hover:bg-oud-900 hover:shadow-quote active:scale-[0.98] transition-all">
            ابدأ تجربتك المجانية
          </Link>
          <a href={getDemoUrl()} target="_blank" rel="noreferrer noopener"
            className="mt-2 text-sm text-oud-500 hover:text-oud-800 transition-colors underline underline-offset-4 decoration-oud-200 hover:decoration-oud-400">
            عرض الموقع التجريبي
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8">
        <div className="max-w-5xl mx-auto px-5 text-center text-sm text-oud-500">
          سنديان © {new Date().getFullYear()} — صنع بـ ♥ في السعودية
        </div>
      </footer>
    </div>
  );
}

const TRUSTED_NAMES = [
  'أثاث الرياض', 'مفروشات الخليج', 'ورشة المجالس', 'تنجيد الدمام',
  'أثاث جدة', 'مصنع الديار', 'بيت الأثاث', 'مجالس التراث',
  'كنب السعودية', 'ورشة الفن', 'أثاث نجد', 'مفروشات لمسة',
  'دار الكنب', 'مصنع الأصيل', 'أثاث المدينة', 'مجالس الضيافة',
];

function TrustedByMarquee() {
  // Duplicate the list so the second copy seamlessly follows the first
  const items = [...TRUSTED_NAMES, ...TRUSTED_NAMES];
  return (
    <section className="py-10 overflow-hidden" dir="ltr">
      <p className="text-center text-xs font-bold text-oud-400 tracking-[0.25em] uppercase mb-6" dir="rtl">يثق بنا</p>
      <div
        style={{
          maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
        }}
      >
        <div className="flex w-max animate-marquee gap-12">
          {items.map((name, i) => (
            <span key={`${i}-${name}`} className="flex-none text-sm font-bold text-oud-400 whitespace-nowrap opacity-50" dir="rtl">{name}</span>
          ))}
        </div>
      </div>
    </section>
  );
}
